<template>
  <div>
    <div v-if="user.uploader_profile" class="alert alert-secondary" role="alert">
      <div class="alert-icon">
        <i class="flaticon-warning" />
      </div>
      <div class="alert-text font-weight-bold">
        Your user has an uploader profile assigned. When your upload is approved an {{ getItemLabel(true) }} will be created.
        <span v-if="user.uploader_profile.approval_required"><br>Your uploads must be approved by a {{ user.customer.name }} user.</span>
        <span v-else>You have permission to approve your own uploads.</span>
      </div>
    </div>
    <f-data-table id="tpu-table" heading="Uploader"
                  icon="flaticon-2-cube"
                  :data-uri="dataUri"
                  :columns="columns"
                  :selectable-rows="true"
                  :server-side="true">
      <template #_actions="props">
        <a type="button"
           class="btn btn-ss"
           href="javascript:void(0);"
           @click="props.reloadData()">
          <i class="flaticon2-refresh" />Reload Data
        </a>
      </template>
      <template #_tools="props">
        <collapse-portlet title="Tools" :expanded="true"
                          :accordion="accordion"
                          :compact="true">
          <uploader-dropzone post-url="/api/v1/uploader/upload-file" :upload-button-classes="[`btn`, `btn-ss-standout`]"
                             mime-types=".mp3"
                             @success="props.reloadData" />
        </collapse-portlet>
      </template>
      <template #_filters>
        <f-data-table-filters target="tpu-table" :filters="filters"
                              filter-layout="col"
                              :expanded="false"
                              :accordion="accordion" />
      </template>
      <template #_row_controls="props">
        <button v-perms="['api.storage.destroy']" class="btn btn-ss-danger" @click="props.do({ cb: deleteAssets, reload_data: true })">
          <i class="la la-trash" /> Delete
        </button>
      </template>
      <template #file_size="props">
        {{ formatBytesAs(props.row.file_size, `MB`) }}
      </template>
      <template #created_at="props">
        {{ formatDateTime(props.row.created_at) }}
      </template>
      <template #actions="props">
        <div class="row-actions">
          <div class="btn-group" role="group">
            <button type="button" class="text-center btn btn-sm btn-ss"
                    title="Listen"
                    @click="listen(props.row)">
              <i class="fa fa-play" />
            </button>
            <button v-if="!user.uploader_profile || ! user.uploader_profile.approval_required" type="button"
                    class="text-center btn btn-sm btn-ss"
                    title="Approve"
                    @click="props.do({ cb: () => checkApprove(props.row) })">
              <i class="fa fa-check-circle" />
            </button>
            <button type="button" class="text-center btn btn-sm btn-ss-danger"
                    title="Delete"
                    @click="props.do({ cb: deleteAssets })">
              <i class="fa fa-trash" />
            </button>
          </div>
        </div>
      </template>
    </f-data-table>
    <third-party-upload-approve-modal :storage-asset="asset_to_approve" :uploader-profile="asset_profile" />
    <simple-html5-player v-if="listenFilePath" :file-path="listenFilePath"
                         :bottom-fixed="true" @close-request="() => listenFilePath = ``" />
  </div>
</template>

<script>
import UploaderDropzone from "VuePartials/UploaderDropzone";
import SimpleHtml5Player from "VueComponents/SimpleHtml5Player";
import CollapsePortlet from 'VueComponents/CollapsePortlet.vue';
import FDataTable from "VuePartials/App/Storage/FDataTable/FDataTable.vue";
import FDataTableFilters from "VuePartials/App/Storage/FDataTable/FDataTableFilters.vue";
import ThirdPartyUploadApproveModal from "VueModals/ThirdPartyUploadApproveModal";

export default {
  components: {
    ThirdPartyUploadApproveModal,
    FDataTable,
    FDataTableFilters,
    UploaderDropzone,
    CollapsePortlet,
    SimpleHtml5Player,
  },
  data: () => ({
    approve_modal: 'third-party-upload-approve',
    dataUri: `/api/v1/third-party-uploader`,
    accordion: `tp-uploader-table`,
    listenFilePath: ``,
    user: window.user,
    asset_to_approve: null,
    columns: [
      {
        display: "Name",
        field: "name",
        never_clip: true,
      },
      {
        display: "File Size",
        field: "file_size",
      },
      {
        display: "Created At",
        field: "created_at",
      },
      {
        display: "Actions",
        field: "actions",
        sortable: false,
        clip_resistance: 1,
        width: 50,
      },
    ],
    filters: [
      {
        name: `name`,
        display: `Name`,
        type: `text`,
      },
    ],
  }),
  computed: {
    asset_profile() {
      return this.asset_to_approve?.uploader_profile;
    },
  },
  methods: {
    async checkApprove(row) {
      if (!row.uploader_profile) {
        return this.approveUpload(row);
      }

      this.asset_to_approve = row;

      const modal_res = await this.fireModal(this.approve_modal);
      if (!modal_res.data.success) {
        this.asset_to_approve = null;
        return false;
      }

      return this.approveUpload(row);
    },
    async approveUpload(row) {
      try {
        (await axios.patch(`/api/v1/third-party-uploader/${row.uuid}/approve`)).data;
        toastr.success("Upload Approved");
        EventBus.$emit("tpu-table-reload");
      } catch (err) {
        window.handleAxiosError(err);
      }
      this.$modal.hide(this.approve_modal);
      this.asset_to_approve = null;
    },
    listen(row) {
      this.listenFilePath = `${window.public_media_path}uploader_storage/${row.name}`;
    },
    async deleteAssets(...assets) {
      try {
        const res = (await axios.delete(`/api/v1/third-party-uploader`, {
          params: {
            ids: assets.map(asset => asset.uuid).join(`,`),
          },
        })).data;
        if (res.success) {
          toastr.success("Assets(s) Deleted");
        } else {
          toastr.error(`Couldn't delete asset(s)!`);
        }
      } catch (err) {
        window.handleAxiosError(err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.row-actions {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  li {
    margin: 0 10px;
    .btn-sm i {
      padding: 0;
    }
  }
}
</style>
